/**
 * Grundton der Oberfläche: hell, dunkel oder der Systemeinstellung folgend.
 *
 * Die Wahl landet als `data-theme` am Wurzelelement — dieselbe Kennung, auf die
 * `index.css` die Farbtokens und die Logo-Fassung umstellt. „system" setzt
 * bewusst **kein** Attribut: Dann greifen die `prefers-color-scheme`-Regeln,
 * und die Anwendung folgt dem Betriebssystem auch, wenn es später wechselt.
 */
import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** Schlüssel im localStorage. Exportiert, damit Tests ihn nicht raten müssen. */
export const THEME_KEY = 'dockly-theme';

const GUELTIG: Theme[] = ['light', 'dark', 'system'];

/**
 * Im privaten Fenster wirft der Zugriff auf localStorage, statt leer zu liefern.
 * Ein Grundton ist keine Funktion, für die eine Seite abstürzen darf.
 */
function lesen(): Theme {
  try {
    const wert = localStorage.getItem(THEME_KEY);
    return GUELTIG.includes(wert as Theme) ? (wert as Theme) : 'system';
  } catch {
    return 'system';
  }
}

function schreiben(theme: Theme): void {
  try {
    if (theme === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, theme);
  } catch {
    // Ohne Speicher gilt die Wahl nur für diese Sitzung — das ist in Ordnung.
  }
}

function systemBevorzugt(): ResolvedTheme {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function anwenden(theme: Theme): void {
  const wurzel = document.documentElement;
  if (theme === 'system') wurzel.removeAttribute('data-theme');
  else wurzel.setAttribute('data-theme', theme);
}

export interface ThemeSteuerung {
  /** Was gewählt ist — einschließlich „system". */
  theme: Theme;
  /** Was tatsächlich zu sehen ist — nie „system". */
  resolved: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

export function useTheme(): ThemeSteuerung {
  const [theme, setThemeState] = useState<Theme>(() => lesen());
  const [system, setSystem] = useState<ResolvedTheme>(() => systemBevorzugt());

  // Beim ersten Rendern gleichziehen: Ein gespeicherter Wert muss auch dann
  // wirken, wenn das Attribut noch nicht am Dokument steht.
  useEffect(() => {
    anwenden(theme);
  }, [theme]);

  // Wechselt das Betriebssystem den Grundton, folgt „system" mit.
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;

    const abfrage = matchMedia('(prefers-color-scheme: dark)');
    const beiWechsel = () => setSystem(abfrage.matches ? 'dark' : 'light');

    abfrage.addEventListener?.('change', beiWechsel);
    return () => abfrage.removeEventListener?.('change', beiWechsel);
  }, []);

  const setTheme = useCallback((neu: Theme) => {
    setThemeState(neu);
    schreiben(neu);
    anwenden(neu);
  }, []);

  return {
    theme,
    resolved: theme === 'system' ? system : theme,
    setTheme,
  };
}

export default useTheme;
